/**
 * 把畫面上的 A4 版面（.print-doc 那一塊）直接輸出成 PDF 檔，不經過列印對話框。
 *
 * 為什麼要有這個：列印對話框存 PDF 每個人的設定不同（縮放、頁首頁尾、背景圖），
 * 存出來的檔案長相不一致；這裡固定用同一組參數產檔，寄出去的版本才穩定。
 *
 * 做法是把版面渲染成圖片再包進 PDF。純前端沒有別的方式能產出檔案；
 * 代價是文字變成圖片，不能選取搜尋、檔案也比較大。
 *
 * 分頁以「元素」為單位算切點，不是把長圖硬切成等高的頁：
 * 硬切一定會從某一列的中間斷開，印出來像掃壞的。
 */

/** 字型與圖片都就緒才動作。字型還沒載入就輸出，字寬與換行都會跑掉 */
export function whenDocReady() {
  return Promise.all([
    document.fonts?.ready ?? Promise.resolve(),
    ...[...document.images].map((img) =>
      img.complete ? Promise.resolve() : new Promise((r) => { img.onload = img.onerror = r })
    ),
  ])
}

export async function downloadElementAsPdf(el: HTMLElement, filename: string) {
  await whenDocReady()
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ])

  /*
   * 截圖前先把只給螢幕看的裝飾關掉（紙張邊框、分頁輔助線），
   * 不然它們會被畫進 PDF——輸出要跟列印版一致。
   *
   * 量測也要在關掉之後做：邊框佔 1px，會讓比例基準跟實際渲染的畫布差一點，
   * 切片位置就會偏個零點幾公釐，剛好卡在表格上緣這種地方就看得出來。
   */
  el.classList.add('pdf-export')
  let canvas: HTMLCanvasElement
  let cuts: number[]
  let mmToPx: number
  try {
    const PAGE_H_MM = 273                             // A4 297mm 扣掉上下各 12mm 邊界
    mmToPx = el.getBoundingClientRect().width / 210    // 版面寬固定 210mm
    const pageHpx = PAGE_H_MM * mmToPx
    const top = el.getBoundingClientRect().top

    /* 每一頁切在哪：逐一累積可分割的區塊（表格、簽核區），
       超過一頁可用高度就退回「上一個區塊的結尾」斷頁，不從列中間切 */
    const blocks = [...el.children].map((c) => {
      const r = c.getBoundingClientRect()
      return { start: r.top - top, end: r.bottom - top }
    })
    cuts = [0]
    let limit = pageHpx
    for (const b of blocks) {
      if (b.end > limit) {
        /* 這個區塊本身就比一整頁高（例如很長的明細表），只能硬切 */
        const cut = b.start > cuts[cuts.length - 1] ? b.start : limit
        cuts.push(cut)
        limit = cut + pageHpx
      }
    }
    cuts.push(el.scrollHeight)

    canvas = await html2canvas(el, {
      /* 3 倍 ≈ 288dpi。2 倍只有 192dpi，中文小字列印會糊；
         再往上檔案大小成長很快，對 email 夾檔不划算 */
      scale: 3,
      backgroundColor: '#fff',
      logging: false,
      windowWidth: el.scrollWidth,
    })
  } finally {
    el.classList.remove('pdf-export')
  }

  /* 垂直比例直接用高度算，不要從寬度推 */
  const ratio = canvas.height / cuts[cuts.length - 1]

  const pdf = new jsPDF({ unit: 'mm', format: 'a4' })
  for (let i = 0; i < cuts.length - 1; i++) {
    const sliceTop = Math.round(cuts[i] * ratio)
    const sliceH = Math.round((cuts[i + 1] - cuts[i]) * ratio)
    if (sliceH <= 0) continue
    /* 把這一段裁到獨立畫布再貼上，jsPDF 才不會把整份重複塞進每一頁 */
    const part = document.createElement('canvas')
    part.width = canvas.width
    part.height = sliceH
    part.getContext('2d')!.drawImage(canvas, 0, sliceTop, canvas.width, sliceH, 0, 0, canvas.width, sliceH)
    const hMm = (sliceH / ratio) / mmToPx
    if (i > 0) pdf.addPage()
    /* 第一頁的上邊界由版面自己的 padding 提供；第二頁起是從內容中間切開的，
       不補這 12mm 的話文字會直接貼在紙張最上緣 */
    pdf.addImage(part.toDataURL('image/jpeg', 0.92), 'JPEG', 0, i === 0 ? 0 : 12, 210, hMm)
  }

  pdf.save(filename)
}

/** 組檔名：空的欄位略過，並拿掉 Windows 不允許出現在檔名裡的字元 */
export function pdfFilename(...parts: (string | null | undefined)[]) {
  return parts
    .filter((p): p is string => !!p?.trim())
    .map((p) => p.trim().replace(/[\/:*?"<>|]/g, '_'))
    .join('_') + '.pdf'
}
